import React, { useEffect, useRef, useState, useContext } from 'react'
import { formatAppointmentDate } from '../../../utils/appointment-time'
import { useHistory, useParams } from 'react-router-dom'
import cn from 'classnames'
import { CustomerContext } from '../../../contexts/CustomerContext'
import Option from '../../../models/Option'
import AttentionBanner from '../../attention-banner'
import AppointmentOption from '../../appointment-option'
import Spinner from '../../Spinner'
import { routes } from '../../../routes'
import classes from './appointment-session-selection.module.sass'
import useQueryString from '../../../hooks/use-query-string'
import { Price, OrderPackage } from '../../../models'
import placeholder from '../../../assets/front_page_form_img.png'
import Image from '../../Image'
import { getOptionName } from '../../../utils/functions'

// ----- Senior-only helpers ---------------------------------------------------
// Maps a feature label (e.g. "Cap & Gown", "30 Poses") to a small SVG. Matches
// the icon set used on the /seniors landing page so the booking flow and the
// landing page share a consistent visual language.
const CapIcon = () => (
  <svg className="seniors-package-card__feature-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
  </svg>
)
const BookIcon = () => (
  <svg className="seniors-package-card__feature-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 4H3c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h6c.55 0 1.05-.22 1.41-.59L12 19l1.59 1.41c.36.37.86.59 1.41.59h6c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM3 19V6h8v13.08L9.92 18H3zm18 0h-6.92L13 19.08V6h8v13z"/>
  </svg>
)
const CameraIcon = () => (
  <svg className="seniors-package-card__feature-icon" viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" aria-hidden="true">
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
    <circle cx="12" cy="13" r="2.6"/>
  </svg>
)

const featureIconFor = (label) => {
  if (/cap.*?gown/i.test(label)) return <CapIcon />
  if (/yearbook.*?pose/i.test(label)) return <BookIcon />
  if (/poses?\b/i.test(label)) return <CameraIcon />
  return null
}

// Parse the "from"-tier out of an upgrade option's name — e.g.
// "Upgrade Standard to Deluxe" → "standard", "E-Upgrade Economy to Deluxe" →
// "economy". Returns null for generic upgrades like "Upgrade Sitting" that
// don't name a source tier; those are treated as always-applicable.
const tierFromUpgradeName = (name) => {
  const cleaned = (name || '').replace(/^[A-Z]-\s*/, '')
  const m = cleaned.match(/upgrade\s+(\w+)\s+to\s+/i)
  return m ? m[1].toLowerCase() : null
}

// Build the feature list for a session card. Mirrors the senior landing logic:
// for the top three tiers (Super / Deluxe / Standard), lead with the pose
// count; for the lower-tier extras (Basic / Economy), keep the original order.
const buildFeatures = (option, isExtra) => {
  const raw = (option.description || '').split(',').map((s) => s.trim()).filter(Boolean)
  if (raw.length === 0) {
    // Fallback to the existing data model if description is empty.
    const fallback = []
    if (option.poses > 0) fallback.push(`${option.poses} Poses`)
    ;(option.imageTypes || []).forEach((it) => fallback.push(it.name))
    return fallback
  }
  if (isExtra) return raw
  const posesIdx = raw.findIndex((item) => /^\d+\s+Poses?$/i.test(item))
  if (posesIdx > 0) return [raw[posesIdx], ...raw.slice(0, posesIdx), ...raw.slice(posesIdx + 1)]
  return raw
}

const SeniorSessionCard = ({ option, i, onSelect, compact = false, upgradeFromPrice = null }) => {
  const [first, second] = getOptionName(option.name)
  const sessionName = [first, second].filter(Boolean).join(' ')
  const tierName = (first || '').toLowerCase()
  const isFeatured = tierName === 'deluxe'
  // Name-based instead of index-based so the shorter Standard silhouette
  // sticks to the actual Standard card no matter where it sits in the grid
  // (e.g. on the upgrade view when only Super + Deluxe are shown).
  const isShorter = tierName === 'standard'
  if (!option.prices || option.prices.length === 0) return null

  const priceVal = Number(option.prices[0].price) || 0
  const dollars = Math.floor(priceVal)
  const cents = String(Math.round((priceVal - dollars) * 100)).padStart(2, '0')
  const features = buildFeatures(option, compact)

  // Upgrade-pricing mode: when `upgradeFromPrice` is provided (the price the
  // customer paid for their previously-booked session), show the tier's
  // regular price with a strikethrough above the actual difference they'd
  // pay to step up — rendered in blue as "Upgrade for $X".
  const isUpgrade = upgradeFromPrice !== null && upgradeFromPrice !== undefined
  const upgradeAmount = isUpgrade ? Math.max(priceVal - upgradeFromPrice, 0) : 0

  const renderPrice = () => {
    if (isUpgrade) {
      return (
        <div className={classes.upgradePriceBlock}>
          <p className={cn('seniors-package-card__price', classes.priceBelow, classes.upgradeOriginalPrice)}>
            <span className="seniors-package-card__currency">$</span>{dollars}
            <span className="seniors-package-card__cents">.{cents}</span>
          </p>
          <p className={classes.upgradeNewPrice}>
            Upgrade for ${upgradeAmount.toFixed(2)}
          </p>
        </div>
      )
    }
    return (
      <p className={cn('seniors-package-card__price', classes.priceBelow)}>
        <span className="seniors-package-card__currency">$</span>{dollars}
        <span className="seniors-package-card__cents">.{cents}</span>
      </p>
    )
  }

  // Bold the digit in "Plus N Personal Outfit(s)" so the difference between
  // Super (Plus 2) and Deluxe (Plus 1) reads at a glance. The site-wide
  // Meyer CSS reset sets `font: inherit` on <strong>, so the browser-default
  // bold is wiped out — we explicitly restore font-weight via a class.
  const renderFeatureLabel = (label) => {
    if (!/personal\s+outfit/i.test(label)) return label
    return label.split(/(\d+)/g).map((part, idx) => (
      /^\d+$/.test(part)
        ? <strong key={idx} className={classes.featureCountBold}>{part}</strong>
        : part
    ))
  }

  const renderFeatures = () => (
    features.length > 0 && (
      <ul className={cn('seniors-package-card__features', classes.featuresLeft)}>
        {features.map((feat) => {
          const icon = featureIconFor(feat)
          return (
            <li key={feat} className={icon ? 'has-feature-icon' : ''}>
              {icon}
              {renderFeatureLabel(feat)}
            </li>
          )
        })}
      </ul>
    )
  )

  // Compact variant — Basic / Economy on the secondary row. No image, smaller
  // padding, designed to be deliberately less enticing than the main tier.
  if (compact) {
    return (
      <div className={cn('seniors-package-card', classes.compactCard)}>
        <h3 className="seniors-package-card__name">{sessionName}</h3>
        {renderFeatures()}
        <div className={classes.compactPriceRow}>
          {renderPrice()}
          <button
            type="button"
            className={cn('seniors-btn', 'seniors-btn--outline', classes.selectBtn)}
            onClick={() => onSelect(option)}
          >
            Select
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('seniors-package-card', classes.seniorCard, {
      'seniors-package-card--featured': isFeatured,
      [classes.seniorCardShorter]: isShorter,
    })}>
      <div className={classes.seniorCardImage}>
        <Image
          alt={sessionName}
          src={option.imageUrl && !option.imageUrl.includes('missing.png') ? option.imageUrl : placeholder}
        />
        {isFeatured && (
          <div className={cn('seniors-package-card__badge', classes.badgeOverImage)}>
            Most Popular
          </div>
        )}
      </div>
      <h3 className="seniors-package-card__name">{sessionName}</h3>
      {renderFeatures()}
      <div className={classes.priceButtonRow}>
        {renderPrice()}
        <button
          type="button"
          className={cn('seniors-btn', 'seniors-btn--outline', classes.selectBtn)}
          onClick={() => onSelect(option)}
        >
          Select
        </button>
      </div>
    </div>
  )
}

// Compact card for the "Upgrade ..." options. No image, no feature list —
// just name, price, and a Select button. Lives in its own section below the
// session grid.
const SeniorUpgradeCard = ({ option, onSelect }) => {
  if (!option.prices || option.prices.length === 0) return null
  const priceVal = Number(option.prices[0].price) || 0
  const dollars = Math.floor(priceVal)
  const cents = String(Math.round((priceVal - dollars) * 100)).padStart(2, '0')
  // Strip a leading "X-" / "X- " prefix from upgrade names so they read cleanly.
  const cleanName = (option.name || '').replace(/^[A-Z]-\s*/, '')
  // Prefer the description field; fall back to tagline so admins can fill in
  // either column in the back office.
  const description = ((option.description || '').trim() || (option.tagline || '').trim())

  return (
    <div className={cn('seniors-package-card', classes.upgradeCard)}>
      <h3 className="seniors-package-card__name">{cleanName}</h3>
      {description && (
        <p className={classes.upgradeDescription}>{description}</p>
      )}
      <p className={cn('seniors-package-card__price', classes.priceBelow)}>
        <span className="seniors-package-card__currency">$</span>{dollars}
        <span className="seniors-package-card__cents">.{cents}</span>
      </p>
      <button
        type="button"
        className={cn('seniors-btn', 'seniors-btn--outline', classes.selectBtn)}
        onClick={() => onSelect(option)}
      >
        Select
      </button>
    </div>
  )
}

// 4-step indicator. Connector lines live between items as their own elements
// so they always start AFTER the previous label and end BEFORE the next icon,
// regardless of label width.
const SeniorSteps = () => (
  <ol className={classes.steps} aria-label="Booking progress">
    <li className={cn(classes.stepItem, classes.stepItemDone)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Find your student</span>
    </li>
    <span className={cn(classes.stepConnector, classes.stepConnectorDone)} aria-hidden="true" />
    <li className={cn(classes.stepItem, classes.stepItemActive)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9"/>
          <circle cx="12" cy="12" r="3" fill="currentColor"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Choose your session</span>
    </li>
    <span className={classes.stepConnector} aria-hidden="true" />
    <li className={classes.stepItem}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
          <line x1="8" y1="3" x2="8" y2="7"/>
          <line x1="16" y1="3" x2="16" y2="7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Choose date/time</span>
    </li>
    <span className={classes.stepConnector} aria-hidden="true" />
    <li className={classes.stepItem}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Confirm</span>
    </li>
  </ol>
)
// ----- end senior helpers ----------------------------------------------------

const AppointmentOptionSelection = () => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const { packageId } = useParams()
  const { o: isOther, senior, preview, upgrade: preselectUpgradeId } = useQueryString()
  const autoUpgradeDone = useRef(false)
  // Temporary visual-preview flag — `?preview=upgrades` forces the returning-
  // customer view even when no prior purchase exists, so the page can be
  // styled without seeding test data. Remove this when we have real returning
  // customers to test with.
  const previewReturning = preview === 'upgrades'
  const history = useHistory()
  const [options, setOptions] = useState([])
  // Prior booking (if any) for the currently identified student against this
  // package. When present, the customer is a *returning* customer and we
  // render the upgrade-only view instead of the full session grid.
  const [priorOrderPackage, setPriorOrderPackage] = useState(null)

  useEffect(() => {
    const priceScope = Price.where({ hasPrice: true });

    let scope = Option
      .includes(['image_types', 'prices'])
      .order('abbreviation')
      .merge({ prices: priceScope })
      .where({ packageId })

    // Preview mode skips the email-based filter so upgrade options come
    // through even without a prior purchased cart.
    if (!previewReturning) {
      scope = scope.where({ hasPurchasedPreviousOption: [(customer.email || '').toLowerCase(), packageId] })
    }

    scope.all().then((res) => setOptions(res.data))
  }, [packageId, previewReturning])

  // Look up the student's most recent purchased OrderPackage for this package.
  // Student-based detection is more accurate than the backend's email-based
  // filter — a parent booking for a second kid using the same email shouldn't
  // be treated as a returning customer for that second kid.
  useEffect(() => {
    const studentId = customer.currentStudent?.id
    if (!studentId) {
      setPriorOrderPackage(null)
      return
    }
    OrderPackage
      .where({ studentId, packageId, purchased: true })
      .order({ createdAt: 'desc' })
      .first()
      .then(({ data }) => {
        setPriorOrderPackage(data || null)
      })
      .catch((err) => {
        console.error('prior order package lookup failed', err)
        setPriorOrderPackage(null)
      })
  }, [customer.currentStudent?.id, packageId])

  const isLocalhost = typeof window !== 'undefined'
    && /^(localhost|127\.|.*\.local)$/.test(window.location.hostname)

  const onSelect = (opt) => {
    const { currentOrderPackage } = customer

    // Preview mode (`?preview=upgrades`) skips the appt-cart/order-package
    // setup, so there's no real OrderPackage to mutate. Surface a friendly
    // alert and skip navigation rather than crashing on `undefined.optionId`.
    if (!currentOrderPackage) {
      if (previewReturning) {
        alert(`Demo: selected "${opt.name}". In the real flow, this would advance to the date/time picker.`)
        return
      }
      console.error('onSelect: customer.currentOrderPackage is undefined')
      return
    }

    currentOrderPackage.optionId = opt.id
    currentOrderPackage.apiOption = opt
    currentOrderPackage.option = opt

    setCustomer({ ...customer, currentOrderPackage })
    history.push(routes.appointmentSelection(senior ? { senior: 'true' } : {}))
  }

  // Upgrade flow — the customer is changing tiers on an *existing* booking,
  // so we skip the date/time picker (their original slot carries over) and
  // route straight to the review page. The new OrderPackage carries the
  // upgrade-to option id (so the photographer captures the higher tier) but
  // with a custom price equal to just the *difference* the customer owes.
  // `priorTierName` (e.g. "Standard") is forwarded to checkout via an
  // `upgradeFrom` query param so the review page can render an "(upgrade
  // from Standard)" indicator under the session name.
  const onSelectUpgrade = (opt, priorOrderPackage, priorPrice, priorTierName) => {
    const { currentOrderPackage } = customer

    if (!currentOrderPackage) {
      if (previewReturning) {
        // Preview: navigate to checkout so the user can see the review page
        // with the picked upgrade reflected (session name + upgrade-only diff
        // price + the mocked prior appointment time).
        const optPrice = Number(opt.prices?.[0]?.price) || 0
        const diff = Math.max(optPrice - priorPrice, 0).toFixed(2)
        const sessionName = getOptionName(opt.name).filter(Boolean).join(' ')
        history.push(routes.checkout({
          app: true,
          senior: 'true',
          demoSession: sessionName,
          demoPrice: diff,
          demoTime: '2026-09-08 09:00 AM',
          ...(priorTierName ? { upgradeFrom: priorTierName } : {}),
        }))
        return
      }
      console.error('onSelectUpgrade: customer.currentOrderPackage is undefined')
      return
    }

    const optPrice = Number(opt.prices?.[0]?.price) || 0

    currentOrderPackage.optionId = opt.id
    currentOrderPackage.apiOption = opt
    currentOrderPackage.option = opt
    currentOrderPackage.price = Math.max(optPrice - priorPrice, 0)
    if (priorOrderPackage?.appointmentTime) {
      currentOrderPackage.appointmentTime = priorOrderPackage.appointmentTime
    }
    currentOrderPackage.appointmentSeason = 'senior'
    // Mark this record as the upgrade purchase so the photo-day workflow
    // can prefer it over the prior (original-tier) OrderPackage when both
    // rows exist for the same student + appointment slot.
    currentOrderPackage.isUpgrade = true

    const checkoutParams = {
      app: true,
      senior: 'true',
      ...(priorTierName ? { upgradeFrom: priorTierName } : {}),
    }

    // On localhost (and in preview mode) skip the persistence step and jump
    // straight to checkout — same pattern the schedule-appointment page uses.
    if (isLocalhost || previewReturning || !customer.apptCart) {
      setCustomer({ ...customer, currentOrderPackage })
      history.push(routes.checkout(checkoutParams))
      return
    }

    currentOrderPackage.cartId = customer.apptCart.id
    currentOrderPackage.schoolId = customer.currentSchool?.id
    currentOrderPackage.save().then((res) => {
      if (!res) {
        console.error('Failed to save upgrade OrderPackage')
        return
      }
      setCustomer({ ...customer, currentOrderPackage })
      history.push(routes.checkout(checkoutParams))
    })
  }

  // Staff-sent upgrade link (`?upgrade=<optionId>`): once options + the prior
  // booking are loaded, auto-select that option — the exact same call tapping the
  // card makes — so the customer lands straight on checkout for the difference.
  // Falls through to the normal upgrade grid if the option isn't offered.
  useEffect(() => {
    if (autoUpgradeDone.current || !preselectUpgradeId || previewReturning) return
    if (!options.length || !priorOrderPackage || !customer.currentOrderPackage) return

    const preOpt = options.find((o) => String(o.id) === String(preselectUpgradeId))
    if (!preOpt) return

    const priorOption = options.find((o) => Number(o.id) === Number(priorOrderPackage.optionId))
    const priorPrice = Number(priorOption?.prices?.[0]?.price) || 0
    const priorTierName = priorOption ? (getOptionName(priorOption.name)[0] || '') : ''

    autoUpgradeDone.current = true
    onSelectUpgrade(preOpt, priorOrderPackage, priorPrice, priorTierName)
  }, [preselectUpgradeId, options, priorOrderPackage, customer.currentOrderPackage, previewReturning])

  if (options.length === 0) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    )
  }

  // Senior flow — restyled to match the /seniors landing page.
  if (senior) {
    const isUpgrade = (opt) => /upgrade/i.test(opt.name || '')
    const sessions = options.filter((opt) => !isUpgrade(opt))
    const upgrades = options.filter(isUpgrade)

    // Returning customer = this student already has a purchased OrderPackage
    // for this package. Show only the upgrade options + a "you're currently
    // booked for X" note. The backend's email-based filter also gates whether
    // upgrade options are present in `options` — if we detect a returning
    // student but the backend stripped upgrades (parent used a different
    // email this time), fall back to the new-customer view so we don't render
    // an empty upgrade grid.
    const isReturning = previewReturning
      ? upgrades.length > 0
      : !!priorOrderPackage && upgrades.length > 0
    const priorOption = priorOrderPackage
      ? options.find((o) => Number(o.id) === Number(priorOrderPackage.optionId))
      : (previewReturning
        // Pick a sensible mock "currently booked" session for the preview —
        // prefer Standard, otherwise the first non-upgrade session in the list.
        ? sessions.find((s) => /standard/i.test(s.name)) || sessions[0] || null
        : null)
    const priorSessionName = priorOption
      ? getOptionName(priorOption.name).filter(Boolean).join(' ')
      : null
    const priorFeatures = priorOption ? buildFeatures(priorOption, false) : []

    // Tier hierarchy (1 = highest, 5 = lowest). Upgrade options are tiers
    // *above* the customer's currently-booked tier.
    const TIER_RANK = { super: 1, deluxe: 2, standard: 3, basic: 4, economy: 5 }
    const tierRank = (name) => TIER_RANK[(getOptionName(name)[0] || '').toLowerCase()] ?? 99
    const priorRank = priorOption ? tierRank(priorOption.name) : 99
    const upgradeSessions = sessions
      .filter((s) => tierRank(s.name) < priorRank)
      .sort((a, b) => tierRank(a.name) - tierRank(b.name))   // best → worst

    const priorPrice = Number(priorOption?.prices?.[0]?.price) || 0

    // Format the prior appointment date+time for the "you've booked X" line.
    // Reads the stored datetime's digits as UTC (Pacific wall-clock-digits-
    // labeled-UTC), the convention every appointment-time site in this app uses.
    const priorApptText = priorOrderPackage?.appointmentTime
      ? formatAppointmentDate(priorOrderPackage.appointmentTime, 'dddd, MMMM D [at] h:mm A')
      : (previewReturning ? 'Tuesday, September 8 at 9:00 AM' : null)

    // Prefer the real identified student's name. In preview mode no student
    // is set, so fall back to a placeholder so the layout still reads cleanly.
    const currentStudent = customer.currentStudent
    const studentDisplayName = currentStudent?.firstName || currentStudent?.lastName
      ? [currentStudent.firstName, currentStudent.lastName].filter(Boolean).join(' ')
      : (previewReturning ? 'Jane Smith' : 'Your student')

    if (isReturning) {
      return (
        <div className={classes.seniorWrap}>
          <SeniorSteps />
          {priorSessionName && (
            <div className={classes.priorSessionBox} role="note">
              <p className={classes.previousSessionLabel}>
                <strong className={classes.featureCountBold}>{studentDisplayName}</strong>
                {' '}has a{' '}
                <strong className={classes.featureCountBold}>{priorSessionName}</strong>
                {' '}booked
                {priorApptText && (
                  <>
                    {' — '}
                    <strong className={classes.featureCountBold}>{priorApptText}</strong>
                  </>
                )}
                .
              </p>
              {priorFeatures.length > 0 && (
                <ul className={classes.priorSessionFeaturesInline}>
                  {priorFeatures.map((feat) => (
                    <li key={feat}>{feat}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {upgradeSessions.length > 0 ? (
            <>
              <p className={classes.upgradePrompt}>
                Choose an upgrade below to enhance your session.
              </p>
              <div className={classes.seniorGrid}>
                {upgradeSessions.map((option, idx) => {
                  const priorTierName = priorOption
                    ? (getOptionName(priorOption.name)[0] || '')
                    : ''
                  return (
                    <SeniorSessionCard
                      key={option.id}
                      option={option}
                      i={idx}
                      onSelect={(opt) => onSelectUpgrade(opt, priorOrderPackage, priorPrice, priorTierName)}
                      upgradeFromPrice={priorPrice}
                    />
                  )
                })}
              </div>
            </>
          ) : (
            <p className={classes.upgradePrompt}>
              You've already booked our top-tier Super Session — no upgrades available.
            </p>
          )}
        </div>
      )
    }

    const mainSessions = sessions.slice(0, 3)    // Super / Deluxe / Standard
    const extraSessions = sessions.slice(3)      // Basic / Economy (and any beyond)

    return (
      <div className={classes.seniorWrap}>
        <SeniorSteps />
        <div className={classes.seniorGrid}>
          {mainSessions.map((option, i) => (
            <SeniorSessionCard key={option.id} option={option} i={i} onSelect={onSelect} />
          ))}
        </div>

        {extraSessions.length > 0 && (
          <div className={classes.seniorGridExtras}>
            {extraSessions.map((option, idx) => (
              <SeniorSessionCard
                key={option.id}
                option={option}
                i={3 + idx}
                onSelect={onSelect}
                compact
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Default (elementary / retake) flow — unchanged.
  return (
    <>
      <div>
        <AttentionBanner content="Step 1. Choose Your Session" />
        <div className={classes.content}>
          <div className={classes.optionsContainer}>
            {options.map((option, i) => (
              <AppointmentOption
                key={option.id}
                option={option}
                handleSelect={onSelect}
                i={i}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default AppointmentOptionSelection
